import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckIcon, ChevronDownIcon } from 'lucide-react';

import { controlBase, controlStates } from '../lib/control';
import { cn } from '../lib/utils';

import { menuOptionCls, menuPanelCls, useAnchoredPanel } from './anchored-panel';

export interface SelectOption {
  value: string;
  label: string;
  /**
   * A second fact set to the right in the open list, every one ending on the
   * same edge: a price beside a package. Not shown in the closed box, which
   * keeps to the label so it fits a narrow row.
   */
  detail?: string;
}

/**
 * Which skin the trigger wears. A dropdown's whole job is to sit in a row of
 * other controls without announcing that it is a different kind of thing:
 *
 *   control  the compact toolbar height, sized to its own value
 *   field    the form skin — Input's own box from `lib/control`, full width
 */
export type SelectVariant = 'control' | 'field';

/** How long a typed run keeps matching before the next key starts a new one. */
const TYPEAHEAD_RESET_MS = 700;

/**
 * The app's dropdown.
 *
 * The panel is portalled and placed against the viewport — see
 * `useAnchoredPanel` for why an absolutely positioned one could not work here.
 *
 * Focus stays on the trigger the whole time and the active option is tracked
 * with `aria-activedescendant` — the alternative, moving focus into the list,
 * means catching focus on the way out of every close path.
 */
export default function Select({
  value,
  options,
  onChange,
  placeholder = 'Select…',
  disabled,
  variant = 'control',
  className,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  onBlur,
}: {
  /** Optional only so a `<Field name>` can supply it; set it yourself outside one. */
  value?: string;
  options: readonly SelectOption[];
  /** Optional only so a `<Field name>` can supply it; set it yourself outside one. */
  onChange?: (next: string) => void;
  /** Shown when `value` matches no option — an unmade choice. */
  placeholder?: string;
  disabled?: boolean;
  /** Defaults to the compact toolbar skin. Use `field` inside a `<Field>`. */
  variant?: SelectVariant;
  className?: string;
  'aria-label'?: string;
  /** Set by a form field, so the trigger announces its hint or error. */
  'aria-describedby'?: string;
  /** Set by a form field whose value failed validation. */
  'aria-invalid'?: boolean;
  /** Fires when focus leaves the trigger. Focus stays on it while the list is open. */
  onBlur?: () => void;
}) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const typed = useRef({ run: '', at: 0 });
  const {
    anchor: trigger,
    panel: list,
    style,
    gapClass,
  } = useAnchoredPanel<HTMLButtonElement, HTMLUListElement>(open);

  const selected = options.findIndex((o) => o.value === value);
  const hasDetails = options.some((o) => o.detail);
  const label = selected >= 0 ? options[selected].label : placeholder;
  const isField = variant === 'field';

  // Pointer down rather than click: a click on another control should close
  // this before that control reacts, not after. The panel is portalled out of
  // the trigger's subtree, so it has to be tested on its own — otherwise
  // picking an option reads as a click on the outside world.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (trigger.current?.contains(target) || list.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open, trigger, list]);

  // Keep the active option in view when the arrows walk past the panel's edge.
  useEffect(() => {
    if (!open) return;
    list.current?.children[active]?.scrollIntoView({ block: 'nearest' });
  }, [open, active, list]);

  const openAt = (index: number) => {
    // An empty list has nothing to pick: stay shut and let the placeholder
    // say why ("Pick a date first"), rather than greying the field out.
    if (disabled || options.length === 0) return;
    setActive(index < 0 ? 0 : index);
    setOpen(true);
  };

  const pick = (index: number) => {
    const option = options[index];
    if (option) onChange?.(option.value);
    setOpen(false);
  };

  /**
   * The index a typed key lands on, or null for no match. A fresh run starts
   * from the option after the active one, so pressing the same letter twice
   * walks the options starting with it; extending a run re-tests the active
   * one first, so "ma" still matches the "March" that "m" just found.
   */
  const typeaheadTarget = (key: string): number | null => {
    const now = Date.now();
    const fresh = now - typed.current.at > TYPEAHEAD_RESET_MS;
    const run = (fresh ? key : typed.current.run + key).toLowerCase();
    typed.current = { run, at: now };

    const from = run.length === 1 ? active + 1 : active;
    for (let i = 0; i < options.length; i += 1) {
      const index = (from + i) % options.length;
      if (options[index].label.toLowerCase().startsWith(run)) return index;
    }
    return null;
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    // Typeahead works either way: closed, it opens on the match rather than
    // committing to it, so a mistyped letter costs nothing.
    if (e.key.length === 1 && e.key !== ' ' && !e.metaKey && !e.ctrlKey && !e.altKey) {
      const to = typeaheadTarget(e.key);
      if (to === null) return;
      e.preventDefault();
      return open ? setActive(to) : openAt(to);
    }

    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openAt(selected);
      }
      return;
    }
    if (e.key === 'Escape') {
      // Or the dialog this sits in takes the same Escape and closes too.
      e.stopPropagation();
      return setOpen(false);
    }
    if (e.key === 'Tab') return setOpen(false);
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      return pick(active);
    }
    const last = options.length - 1;
    const to =
      e.key === 'ArrowDown'
        ? Math.min(active + 1, last)
        : e.key === 'ArrowUp'
          ? Math.max(active - 1, 0)
          : e.key === 'Home'
            ? 0
            : e.key === 'End'
              ? last
              : null;
    if (to === null) return;
    e.preventDefault();
    setActive(to);
  };

  return (
    <>
      <button
        ref={trigger}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        aria-activedescendant={open ? `${id}-${active}` : undefined}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid}
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openAt(selected))}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        className={cn(
          isField
            ? // The same box, states and height as Input, so it lines up with
              // the text fields beside it.
              [controlBase, controlStates, 'h-9 items-center justify-between gap-2 text-left']
            : [
                'flex h-(--target-control) max-w-full items-center justify-between gap-2 rounded-xl border border-ink/25 bg-surface px-4 text-base text-ink transition-colors',
                'focus-visible:border-brand-edge focus-visible:outline-(length:--focus-ring-width) focus-visible:outline-offset-(--focus-ring-offset) focus-visible:outline-(color:--focus-ring-color)',
                'aria-invalid:border-destructive aria-invalid:bg-danger-wash',
                'disabled:cursor-not-allowed disabled:opacity-55',
                !disabled && 'hover:border-brand-edge',
              ],
          selected < 0 && 'text-ink-muted',
          className,
        )}
      >
        <span className="truncate">{label}</span>
        <ChevronDownIcon
          aria-hidden="true"
          className={cn(
            'size-4 shrink-0 text-ink-muted transition-transform',
            open && 'rotate-180',
          )}
        />
      </button>

      {open &&
        createPortal(
          <ul
            ref={list}
            id={id}
            role="listbox"
            aria-label={ariaLabel}
            style={style}
            className={cn(
              menuPanelCls,
              gapClass,
              // With details, three columns (label, detail, tick) every row
              // shares by `subgrid`, so each detail starts on the same edge:
              // the column is as wide as the widest.
              hasDetails && 'grid grid-cols-[minmax(0,1fr)_auto_auto]',
            )}
          >
            {options.map((option, i) => {
              const isSelected = i === selected;
              return (
                <li
                  key={option.value}
                  id={`${id}-${i}`}
                  role="option"
                  aria-selected={isSelected}
                  onPointerEnter={() => setActive(i)}
                  onClick={() => pick(i)}
                  className={cn(
                    menuOptionCls,
                    hasDetails && 'col-span-3 grid grid-cols-subgrid',
                    i === active && 'bg-menu-option-active-bg text-menu-option-active-fg',
                    isSelected && 'bg-menu-option-selected-bg text-menu-option-selected-fg',
                  )}
                >
                  <span className="min-w-0 truncate">{option.label}</span>
                  {/* Smaller than the label: the fact beside it, not a second name. */}
                  {hasDetails && (
                    <span className="text-sm whitespace-nowrap tabular-nums">
                      {option.detail}
                    </span>
                  )}
                  {/* The tick carries the selected state on its own. Bolding
                      the label instead re-measures the row, so the list
                      twitched every time the value changed. */}
                  <CheckIcon
                    aria-hidden="true"
                    className={cn('size-4 shrink-0', !isSelected && 'invisible')}
                  />
                </li>
              );
            })}
          </ul>,
          document.body,
        )}
    </>
  );
}
